import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { PCTenant } from '../types';

export interface PCTenantsModel {
  pcTenants: Record<string, PCTenant>;
  setPCTenant: Action<PCTenantsModel, PCTenant>;
  setPCTenants: Action<PCTenantsModel, PCTenant[]>;
  removePCTenant: Action<PCTenantsModel, string>;
  pcTenantFailure: Action<PCTenantsModel, ApplicationErrorOptions>;
  fetchPCTenant: Thunk<PCTenantsModel, string, Injections, Promise<PCTenant>>;
  fetchPCTenants: Thunk<PCTenantsModel, void, Injections, Promise<PCTenant[]>>;
  createPCTenant: Thunk<PCTenantsModel, PCTenant, Injections, Promise<PCTenant>>;
  deletePCTenant: Thunk<PCTenantsModel, string, Injections, Promise<PCTenant>>;
  updatePCTenant: Thunk<PCTenantsModel, PCTenant, Injections, Promise<PCTenant>>;
}

export const tenantsModel: PCTenantsModel = {
  pcTenants: {},
  pcTenantFailure: action((state, payload) => {
    console.error(payload);
  }),
  setPCTenants: action((state, payload) => {
    state.pcTenants = {};
    for (const val of payload) {
      state.pcTenants[val.tenantId] = val;
    }
  }),
  setPCTenant: action((state, payload) => {
    state.pcTenants = { ...state.pcTenants, [payload.tenantId]: payload };
  }),
  removePCTenant: action((state, payload) => {
    const updatedTenants = { ...state.pcTenants };
    delete updatedTenants[payload];
    state.pcTenants = updatedTenants;
  }),
  fetchPCTenants: thunk(async (actions, payload, { injections }) => {
    try {
      const tenants = await injections.partnerConfigurationService.fetchPCTenants();
      actions.setPCTenants(tenants?.results ?? []);
      return tenants;
    } catch (error) {
      actions.pcTenantFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching tenants',
        asNotification: true,
      });
    }
  }),
  fetchPCTenant: thunk(async (actions, payload, { injections }) => {
    try {
      const tenant = await injections.partnerConfigurationService.fetchPCTenant(payload);
      actions.setPCTenant(tenant);
      return tenant;
    } catch (error) {
      actions.pcTenantFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching tenant.',
        asNotification: true,
      });
    }
  }),
  createPCTenant: thunk(async (actions, payload, { injections }) => {
    try {
      const tenant = await injections.partnerConfigurationService.createPCTenant(payload);
      actions.setPCTenant(tenant);
      return tenant;
    } catch (error) {
      actions.pcTenantFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating tenant.',
        asNotification: true,
      });
    }
  }),
  updatePCTenant: thunk(async (actions, payload, { injections }) => {
    try {
      const tenant = await injections.partnerConfigurationService.updatePCTenant(payload);
      actions.setPCTenant(tenant);
      return tenant;
    } catch (error) {
      actions.pcTenantFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating tenant.',
        asNotification: true,
      });
    }
  }),
  deletePCTenant: thunk(async (actions, payload, { injections }) => {
    try {
      const tenant = await injections.partnerConfigurationService.deletePCTenant(payload);
      actions.removePCTenant(tenant.tenantId);
      return payload;
    } catch (error) {
      actions.pcTenantFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured deleting tenant.',
        asNotification: true,
      });
    }
  }),
};
